// src/auth/cognitoAuth.ts hardened/refactored v1

import {
  signUp,
  confirmSignUp,
  signIn,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updateUserAttributes,
  confirmUserAttribute,
} from 'aws-amplify/auth';
import type { SignInOutput } from 'aws-amplify/auth';

// ============================================================================
// Auth token
// ============================================================================

export const getAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
};

// ============================================================================
// Sign up
// ============================================================================

interface SignUpResponse {
  isSignUpComplete?: boolean;
  nextStep?: {
    signUpStep?: string;
    codeDeliveryDetails?: {
      destination?: string;
      deliveryMedium?: string;
      attributeName?: string;
    };
  };
  userId?: string;
}

export async function signUpUser(email: string, password: string): Promise<SignUpResponse> {
  const result = await signUp({
    username: email,
    password,
    options: {
      userAttributes: { email },
    },
  });
  return result as SignUpResponse;
}

// ============================================================================
// Confirm sign up
// ============================================================================

export async function confirmSignUpUser(
  email: string,
  code: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await confirmSignUp({ username: email, confirmationCode: code });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to confirm sign-up' };
  }
}

// ============================================================================
// Sign in
// ============================================================================

export async function signInUser(email: string, password: string): Promise<SignInOutput> {
  return signIn({ username: email, password });
}

// ============================================================================
// Resend verification code
// ============================================================================

export async function resendVerificationCode(
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await resendSignUpCode({ username: email });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to resend code' };
  }
}

// ============================================================================
// Password reset
// ============================================================================

export async function initiatePasswordReset(
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await resetPassword({ username: email });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to send reset code' };
  }
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to reset password' };
  }
}

// ============================================================================
// Primary email update
// ============================================================================

export async function updatePrimaryEmail(
  newEmail: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await updateUserAttributes({ userAttributes: { email: newEmail } });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to update email' };
  }
}

export async function verifyEmailUpdate(
  code: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to verify email' };
  }
}